import React, { useMemo } from "react";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine, Cell } from "recharts";
import {
  getOrBuildCache,
  getPrimaryCashZeroInRange,
  formatCashZeroDate,
  ZERO_CASH_MARKER_COLOR,
} from "../../utils/chart/financial-cache.utils";
import { calculateBurndownSnapshot } from "../../utils/chart/burndown.utils";
import {
  formatChartPeriodLabel,
  getChartPeriodData,
  resolveProjectEndChartMarker,
  resolveOverlappingReferenceLabelPlacements,
  computeReferenceLabelChartMargins,
} from "../../utils/chart/chart-period.utils";
import ProjectEndReferenceLine from "./ProjectEndReferenceLine";
import type { Revenue, Expense } from "../../types/startup.types";
import { CHART_COLORS } from "@/constants/chart-colors";

// Using main type definitions from startup.types.ts

interface EventData {
  id: string;
  date: Date;
  type: "funding" | "milestone" | "launch" | "asset" | "custom";
  priority: "high" | "medium" | "low";
  description: string;
  amount?: number;
  icon?: string;
  color?: string;
  // Asset-specific fields
  monthlyAppreciationRate?: number;
  minimumValue?: number;
}

interface ChartData {
  startDate: Date;
  endDate: Date;
  projectEndDate?: Date;
  initialBalance: number;
  expenses: Expense[];
  revenues?: Revenue[];
  events: EventData[];
  customerProjectionData?: import("../../utils/chart/customer-projection.utils").CustomerProjectionData;
}

interface CashFlowBarChartProps {
  data: ChartData;
  visibleDateRange: {
    startDate: Date;
    endDate: Date;
  };
  currentDate?: Date;
  height?: number;
}

const CashFlowBarChart: React.FC<CashFlowBarChartProps> = ({
  data,
  visibleDateRange,
  currentDate = new Date(),
  height = 160,
}) => {
  const { chartData, zeroCashMarker, projectEndMarker } = useMemo(() => {
    const cache = getOrBuildCache(data);

    const rangeData = getChartPeriodData(
      cache,
      data as Parameters<typeof getChartPeriodData>[1],
      visibleDateRange.startDate,
      visibleDateRange.endDate,
    );

    const chartPoints = rangeData.map((periodData) => ({
      month: formatChartPeriodLabel(periodData),
      availableCash: Math.round(periodData.cumulativeCash),
      revenue: Math.round(periodData.revenue),
      expenses: Math.round(periodData.expenses + periodData.oneTimeExpenses),
      funding: Math.round(periodData.funding),
      assetAcquisitions: Math.round(periodData.assetAcquisitions),
      date: new Date(periodData.month),
      periodStart: periodData.periodStart,
      periodEnd: periodData.periodEnd,
    }));

    const primaryZero = getPrimaryCashZeroInRange(
      cache,
      visibleDateRange.startDate,
      visibleDateRange.endDate,
      currentDate,
      calculateBurndownSnapshot(
        data as Parameters<typeof calculateBurndownSnapshot>[0],
        currentDate,
      ).cashBalance,
    );

    let zeroMarker: { periodLabel: string; date: Date; label: string } | null =
      null;

    if (primaryZero) {
      const containingPeriod = chartPoints.find(
        (point) =>
          primaryZero.date >= point.periodStart &&
          primaryZero.date <= point.periodEnd,
      );

      if (containingPeriod) {
        zeroMarker = {
          periodLabel: containingPeriod.month,
          date: primaryZero.date,
          label: formatCashZeroDate(primaryZero.date),
        };
      }
    }

    const periodBounds = rangeData.map((periodData) => ({
      label: formatChartPeriodLabel(periodData),
      periodStart: periodData.periodStart,
      periodEnd: periodData.periodEnd,
    }));

    const projectEndMarker = resolveProjectEndChartMarker(
      data.projectEndDate,
      visibleDateRange.startDate,
      visibleDateRange.endDate,
      periodBounds,
    );

    return { chartData: chartPoints, zeroCashMarker: zeroMarker, projectEndMarker };
  }, [data, visibleDateRange, currentDate]);

  // Custom tooltip
  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div className="bg-surface-elevated text-white text-xs px-3 py-2 rounded-lg shadow-glow">
          <p className="font-semibold">{label}</p>
          <div className="space-y-1 mt-1">
            <p className="text-brand">Revenue: ${Math.round(data.revenue).toLocaleString()}</p>
            {data.funding > 0 && (
              <p className="text-blue-400">Funding: ${Math.round(data.funding).toLocaleString()}</p>
            )}
            {data.assetAcquisitions > 0 && (
              <p className="text-purple-400">Asset Acquired: ${Math.round(data.assetAcquisitions).toLocaleString()}</p>
            )}
            <p className="text-expense">Expenses: ${Math.round(data.expenses).toLocaleString()}</p>
            <p className={`font-medium ${(data.revenue + data.funding + data.assetAcquisitions - data.expenses) >= 0 ? 'text-brand' : 'text-expense'}`}>
              Net: ${Math.round(data.revenue + data.funding + data.assetAcquisitions - data.expenses).toLocaleString()}
            </p>
            <div className="border-t border-white/15 pt-1">
              <p className={`font-bold ${data.availableCash >= 0 ? 'text-brand' : 'text-expense'}`}>
                Available Cash: ${Math.round(data.availableCash).toLocaleString()}
              </p>
            </div>
          </div>
        </div>
      );
    }
    return null;
  };

  const periodLabels = useMemo(
    () => chartData.map((point) => point.month),
    [chartData],
  );

  const referenceLabelPlacements = useMemo(
    () =>
      resolveOverlappingReferenceLabelPlacements(
        zeroCashMarker?.periodLabel,
        projectEndMarker?.periodLabel,
        periodLabels,
      ),
    [zeroCashMarker?.periodLabel, projectEndMarker?.periodLabel, periodLabels],
  );

  const referenceLabelMargins = useMemo(
    () =>
      computeReferenceLabelChartMargins(
        zeroCashMarker ? referenceLabelPlacements.zero : null,
        projectEndMarker ? referenceLabelPlacements.projectEnd : null,
      ),
    [
      zeroCashMarker,
      projectEndMarker,
      referenceLabelPlacements.zero,
      referenceLabelPlacements.projectEnd,
    ],
  );

  const chartHeight =
    height + referenceLabelMargins.top + referenceLabelMargins.bottom;

  return (
    <div className="sh-panel overflow-hidden">
      <div className="border-b border-white/10 px-4 py-2 bg-surface-secondary/60">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h4 className="text-sm font-medium text-white/75">
            Available Cash ({chartData.length} months)
          </h4>
          {zeroCashMarker ? (
            <span className="rounded-md border border-warn/50 bg-warn/10 px-2 py-0.5 text-xs font-semibold text-warn">
              Zero cash · {zeroCashMarker.label}
            </span>
          ) : null}
        </div>
        <div className="flex items-center gap-4 mt-1 text-xs text-white/45">
          <div className="flex items-center gap-1">
            <div className="w-3 h-3 bg-green-500 rounded"></div>
            <span>Above Zero</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-3 h-3 bg-expense rounded"></div>
            <span>Below Zero</span>
          </div>
        </div>
      </div>
      <div className="p-4 pt-2">
        <ResponsiveContainer width="100%" height={chartHeight}>
          <BarChart
            data={chartData}
            margin={{
              top: 12 + referenceLabelMargins.top,
              right: 10,
              left: 10,
              bottom: 10 + referenceLabelMargins.bottom,
            }}
          >
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
            <XAxis 
              dataKey="month" 
              tick={{ fontSize: 11, fill: 'rgba(255,255,255,0.5)' }}
              axisLine={false}
              tickLine={false}
            />
            <YAxis 
              tick={{ fontSize: 11, fill: 'rgba(255,255,255,0.5)' }}
              axisLine={false}
              tickLine={false}
              tickFormatter={(value) => {
                if (Math.abs(value) >= 1000000) {
                  return `$${(value / 1000000).toFixed(1)}M`;
                } else if (Math.abs(value) >= 1000) {
                  return `$${(value / 1000).toFixed(0)}K`;
                } else {
                  return `$${value}`;
                }
              }}
            />
            <Tooltip content={<CustomTooltip />} />
            <ReferenceLine y={0} stroke="rgba(255,255,255,0.25)" strokeWidth={1} />
            {zeroCashMarker ? (
              <ReferenceLine
                x={zeroCashMarker.periodLabel}
                stroke={ZERO_CASH_MARKER_COLOR}
                strokeWidth={3}
                strokeDasharray="6 4"
                label={{
                  value: `Zero · ${zeroCashMarker.label}`,
                  position: referenceLabelPlacements.zero.position,
                  dy: referenceLabelPlacements.zero.dy,
                  fill: ZERO_CASH_MARKER_COLOR,
                  fontSize: 11,
                  fontWeight: 700,
                }}
              />
            ) : null}
            {projectEndMarker ? (
              <ProjectEndReferenceLine
                periodLabel={projectEndMarker.periodLabel}
                labelPlacement={referenceLabelPlacements.projectEnd}
              />
            ) : null}
            <Bar 
              dataKey="availableCash" 
              radius={[2, 2, 0, 0]}
              stroke="none"
            >
              {chartData.map((entry, index) => (
                <Cell 
                  key={`cell-${index}`} 
                  fill={entry.availableCash >= 0 ? CHART_COLORS.cashPositive : CHART_COLORS.cashNegative} 
                />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export default CashFlowBarChart;
